import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = {
  slug: "", name: "", tagline: "", description: "", price: 0, currency: "EUR",
  category: "accessory", images: [], features: [], stock: 100, sku: "", weight_g: 500,
  active: true, featured: false,
};

export function ProductEditor({ open, onClose, product, client, onSaved }) {
  const editing = !!product?.id;
  const [f, setF] = useState(product ? {
    ...EMPTY,
    ...product,
    images: (product.images || []).join("\n"),
    features: (product.features || []).join("\n"),
  } : { ...EMPTY, images: "", features: "" });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        price: parseFloat(f.price) || 0,
        stock: parseInt(f.stock) || 0,
        weight_g: parseInt(f.weight_g) || 500,
        images: (f.images || "").split("\n").map((s) => s.trim()).filter(Boolean),
        features: (f.features || "").split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (editing) await client.put(`/products/${product.id}`, payload);
      else await client.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing || !window.confirm("Delete this product? This cannot be undone.")) return;
    setSaving(true);
    try {
      await client.delete(`/products/${product.id}`);
      toast.success("Product deleted");
      onSaved?.();
      onClose?.();
    } catch { toast.error("Delete failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] text-white border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1"><span className="text-xs text-white/60">Slug</span><Input data-testid="pe-slug" value={f.slug} onChange={(e) => set("slug", e.target.value)} className="bg-white/5 border-white/10" /></label>
            <label className="space-y-1"><span className="text-xs text-white/60">SKU</span><Input value={f.sku} onChange={(e) => set("sku", e.target.value)} className="bg-white/5 border-white/10" /></label>
          </div>
          <label className="space-y-1 block"><span className="text-xs text-white/60">Name</span><Input data-testid="pe-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10" /></label>
          <label className="space-y-1 block"><span className="text-xs text-white/60">Tagline</span><Input value={f.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="bg-white/5 border-white/10" /></label>
          <label className="space-y-1 block"><span className="text-xs text-white/60">Description</span><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="bg-white/5 border-white/10 min-h-[100px]" /></label>
          <div className="grid grid-cols-3 gap-3">
            <label className="space-y-1"><span className="text-xs text-white/60">Price (€)</span><Input data-testid="pe-price" type="number" step="0.01" value={f.price} onChange={(e) => set("price", e.target.value)} className="bg-white/5 border-white/10" /></label>
            <label className="space-y-1"><span className="text-xs text-white/60">Stock</span><Input type="number" value={f.stock} onChange={(e) => set("stock", e.target.value)} className="bg-white/5 border-white/10" /></label>
            <label className="space-y-1"><span className="text-xs text-white/60">Weight (g)</span><Input type="number" value={f.weight_g} onChange={(e) => set("weight_g", e.target.value)} className="bg-white/5 border-white/10" /></label>
          </div>
          <label className="space-y-1 block">
            <span className="text-xs text-white/60">Category</span>
            <Select value={f.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] text-white border-white/10">
                <SelectItem value="kit">Kit</SelectItem>
                <SelectItem value="accessory">Accessory</SelectItem>
                <SelectItem value="bundle">Bundle</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label className="space-y-1 block"><span className="text-xs text-white/60">Images (one URL per line)</span><Textarea value={f.images} onChange={(e) => set("images", e.target.value)} className="bg-white/5 border-white/10 font-mono text-xs min-h-[60px]" /></label>
          <label className="space-y-1 block"><span className="text-xs text-white/60">Features (one per line)</span><Textarea value={f.features} onChange={(e) => set("features", e.target.value)} className="bg-white/5 border-white/10 text-xs min-h-[60px]" /></label>
          <div className="flex gap-6">
            <label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} /><span className="text-sm">Active</span></label>
            <label className="flex items-center gap-2"><Switch checked={f.featured} onCheckedChange={(v) => set("featured", v)} /><span className="text-sm">Featured</span></label>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="ghost" onClick={remove} disabled={saving} data-testid="pe-delete" className="text-red-400 hover:text-red-300">Delete</Button>}
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="pe-save" className="bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold">{saving ? "Saving…" : editing ? "Save changes" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_T = { name: "", role: "", quote: "", image: "", language: "pt", order: 100, published: true };

export function TestimonialEditor({ open, onClose, testimonial, client, onSaved }) {
  const editing = !!testimonial?.id;
  const [f, setF] = useState(testimonial || EMPTY_T);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: f.name,
        role: f.role || "",
        quote: f.quote || null,
        image: f.image || null,
        language: f.language || null,
        order: parseInt(f.order) || 100,
        published: !!f.published,
      };
      if (editing) await client.put(`/admin/testimonials/${testimonial.id}`, payload);
      else await client.post("/admin/testimonials", payload);
      toast.success("Testimonial saved");
      onSaved?.();
      onClose?.();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!editing || !window.confirm("Delete this testimonial?")) return;
    try { await client.delete(`/admin/testimonials/${testimonial.id}`); toast.success("Deleted"); onSaved?.(); onClose?.(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] text-white border-white/10 max-w-xl">
        <DialogHeader><DialogTitle>{editing ? "Edit testimonial" : "New testimonial"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-3">
          <label className="space-y-1 block"><span className="text-xs text-white/60">Name</span><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10" /></label>
          <label className="space-y-1 block"><span className="text-xs text-white/60">Role</span><Input value={f.role || ""} onChange={(e) => set("role", e.target.value)} className="bg-white/5 border-white/10" /></label>
          <label className="space-y-1 block"><span className="text-xs text-white/60">Image URL</span><Input value={f.image || ""} onChange={(e) => set("image", e.target.value)} className="bg-white/5 border-white/10" /></label>
          <label className="space-y-1 block"><span className="text-xs text-white/60">Quote (leave empty to keep slot as "Testimonial coming soon")</span><Textarea value={f.quote || ""} onChange={(e) => set("quote", e.target.value)} className="bg-white/5 border-white/10 min-h-[100px]" /></label>
          <div className="grid grid-cols-3 gap-3">
            <label className="space-y-1"><span className="text-xs text-white/60">Language</span><Input value={f.language || ""} onChange={(e) => set("language", e.target.value)} placeholder="pt/en/es" className="bg-white/5 border-white/10" /></label>
            <label className="space-y-1"><span className="text-xs text-white/60">Order</span><Input type="number" value={f.order || 100} onChange={(e) => set("order", e.target.value)} className="bg-white/5 border-white/10" /></label>
            <label className="flex items-end gap-2"><Switch checked={!!f.published} onCheckedChange={(v) => set("published", v)} /><span className="text-sm">Published</span></label>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="ghost" onClick={remove} className="text-red-400 hover:text-red-300">Delete</Button>}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
